import { useEffect, useRef, useState } from 'react';
import EbookTableOfContents from './EbookTableOfContents';
import EbookSidebarCta from './EbookSidebarCta';
import EbookSidebarDownload from './EbookSidebarDownload';

// The sidebar itself scrolls with the page (it is NOT sticky as a whole). The table of
// contents starts in normal flow and scrolls away with the CTA and the download box.
// Only once the download box has scrolled off the top of the viewport does the TOC turn
// into a sticky nav and fade back in — so the chapter list follows you while you read.
// Mirrors the blueprint's IntersectionObserver on `.download-box`.
const DESKTOP_QUERY = '(min-width: 1280px)';
const FADE_MS = 350;

const EbookSidebar = ({ content, sidebarCta, sidebarDownload, slug, pdfUrl }) => {
  const downloadRef = useRef(null);
  const hideTimer = useRef(null);
  const [sticky, setSticky] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const desktop = window.matchMedia(DESKTOP_QUERY);
    let observer = null;

    const clearHideTimer = () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };

    const disable = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      clearHideTimer();
      setSticky(false);
      setShown(false);
    };

    const enable = () => {
      const target = downloadRef.current;
      if (!target) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            // Download box left past the top → make the TOC sticky and fade it in.
            clearHideTimer();
            setSticky(true);
            requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
          } else if (entry.isIntersecting) {
            // Download box is back in view → fade the sticky TOC out, then drop sticky.
            setShown(false);
            clearHideTimer();
            hideTimer.current = setTimeout(() => setSticky(false), FADE_MS);
          }
        },
        { threshold: 0 }
      );
      observer.observe(target);
    };

    const sync = () => {
      disable();
      if (desktop.matches) enable();
    };

    sync();
    desktop.addEventListener('change', sync);

    return () => {
      desktop.removeEventListener('change', sync);
      disable();
    };
  }, [content, sidebarDownload]);

  return (
    <aside className="relative isolate flex flex-col gap-6">
      {/* Decorative blurred blobs behind the sidebar (desktop only). Mirrors the blueprint's
          `.sidebar-blobs` — a purple + blue glow that sits behind the TOC / CTA stack. */}
      <div className="pointer-events-none absolute inset-0 -z-10 hidden xl:block" aria-hidden="true">
        <div
          className="absolute -top-[170px] -right-5 h-[260px] w-[260px] rounded-full"
          style={{ background: '#9f5cf0', filter: 'blur(65px)', opacity: 0.22 }}
        />
        <div
          className="absolute -top-8 -left-2.5 h-[200px] w-[200px] rounded-full"
          style={{ background: '#3963ff', filter: 'blur(60px)', opacity: 0.18 }}
        />
      </div>

      <EbookTableOfContents content={content} sticky={sticky} shown={shown} />
      {sidebarCta && <EbookSidebarCta {...sidebarCta} />}
      {sidebarDownload && (
        <div ref={downloadRef}>
          <EbookSidebarDownload {...sidebarDownload} pdfUrl={pdfUrl} slug={slug} />
        </div>
      )}
    </aside>
  );
};

export default EbookSidebar;
